import { useEffect, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { fetchUserIssues } from "../../store/Slices/IssuesSlice";
import { closedIcon, highPriorityIcon, inProgressIcon, lowPriorityIcon, mediumPriorityIcon, openIcon, resolvedIcon } from "../../utils/svgs";
import CustomModal from "../CustomModal";
import MultiLineText from "../MultiLineText";
import NewTableComponent from "../NewTableComponent";
import UserAvatar from "../UserAvatar";

const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
}

const columnsTemplate = [
    {
        ...commonColumns,
        name: "Issue ID",
        key: "id",
        selector: (row) => (
            <span
                style={{ cursor: 'default' }}
            >
                {row?.service_issue_id}
            </span>
        ),
        width: "100px",
        tableColumn: true,
        filterName: "issueIdFilter",
        type: "text",
    },
    {
        ...commonColumns,
        name: "Vehicle",
        key: "vehicle",
        selector: (row, index) => {
            return (
                <div
                    style={{ display: "inline-block", overflow: "visible", textTransform: "capitalize" }}
                >
                    {row?.vehicle?.registration_number}
                </div>
            )
        },
        width: "100px",
        tableColumn: true,
        filterName: "vehicleFilter",
        type: "text",
    },
    {
        ...commonColumns,
        name: "Service Entry",
        key: "service_entry",
        modifyData: (row) => row?.services,
        tableColumn: true,
        filterName: "servicesFilter",
        type: "text",
        width: "190px",
        selector: (row, index) => {
            const serviceItems = row?.services || [];

            if (serviceItems.length === 0) {
                return <span className="no-items">No SE</span>;
            }

            if (serviceItems.length === 1) {
                return (
                    <span className="service-task">
                        <MultiLineText text={serviceItems[0]?.service_id} maxWidth={"90px"} />
                    </span>
                );
            }

            const firstItem = serviceItems[0]?.service_id || 'N/A';
            const remainingCount = serviceItems.length - 1;

            return (
                <div className="service-tasks-container">
                    <span className="first-task">
                        <MultiLineText text={firstItem} maxWidth={"90px"} />
                    </span>

                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                <div>
                                    {serviceItems.slice(1).map((item, idx) => (
                                        <div key={idx} className='tooltip_item'>{item?.service_id}</div>
                                    ))}
                                </div>
                            </Tooltip>
                        }
                    >
                        <span className="additional-tasks">+{remainingCount}</span>
                    </OverlayTrigger>

                </div>
            );
        },
    },
    {
        ...commonColumns,
        name: "Title & Description",
        key: "description",
        tableColumn: false,
        selector: (row) => {
            const summaryText = row?.summery?.length > 30 ? row?.summery?.slice(0, 30) + '...' : row?.summery
            return (
                <div>
                    <div>
                        <span
                            style={{ cursor: 'default' }}>
                            {summaryText}</span>
                    </div>
                </div>
            );
        },
        width: "250px",
    },
    {
        ...commonColumns,
        name: "Tags",
        key: "tags",
        tableColumn: true,
        filterName: "tagsFilter",
        type: "text",
        selector: (row, index) => {
            const serviceItems = row?.issue_tags || [];

            if (serviceItems.length === 0) {
                return <span className="no-items">No Tags</span>;
            }

            return (
                <div
                    className="service-tasks-container"
                    style={{ cursor: "pointer" }}>
                    {serviceItems.length === 1 ? (
                        <span className="service-task">
                            <MultiLineText text={serviceItems[0]?.tag_name} maxWidth={"90px"} />
                        </span>
                    ) : (
                        <>
                            <span className="first-task">
                                <MultiLineText text={serviceItems[0]?.tag_name || 'N/A'} maxWidth={"90px"} />
                            </span>
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>
                                        <div>
                                            {serviceItems.slice(1).map((item, idx) => (
                                                <div key={idx} className='tooltip_item'>{item?.tag_name}</div>
                                            ))}
                                        </div>
                                    </Tooltip>
                                }
                            >
                                <span className="additional-tasks">+{serviceItems.length - 1}</span>
                            </OverlayTrigger>
                        </>
                    )}
                </div>
            );
        },
        width: "170px",
    },
    {
        ...commonColumns,
        name: "Priority",
        key: "priority",
        modifyData: (row) => row?.priority,
        tableColumn: true,
        filterName: "priorityFilter",
        type: "text",
        width: "120px",
        selector: (row, index) => (
            <div
                className={`priority ${row?.priority}`}
                style={{ cursor: "pointer" }}
            >
                <div className='d-flex align-items-center gap-2'>
                    <span>
                        {row?.priority === 'low' ? lowPriorityIcon : row?.priority === 'medium' ? mediumPriorityIcon : row?.priority === 'high' ? highPriorityIcon : null}
                    </span>
                    {row?.priority}
                </div>
            </div>
        ),
    },
    {
        ...commonColumns,
        name: "Meter Reading (km)",
        key: "odo_meter",
        selector: (row) => row?.odo_meter,
        modifyData: (row) => row?.odo_meter,
        tableColumn: true,
        filterName: "odoMeterFilter",
        type: "number",
        width: "170px",
    },
    {
        ...commonColumns,
        name: "Reporter",
        key: "reporter",
        selector: (row, index) => {
            return (
                <div style={{ display: "inline-block", overflow: "visible", textTransform: "capitalize" }}>
                    <div className='d-flex align-items-center' style={{ cursor: "pointer" }}>
                        <UserAvatar username={row?.reporter?.username} image={row?.reporter?.image} marginRight="7px" />
                        <span className="hyperlink">{row?.reporter?.username}</span>
                    </div>
                </div>
            )
        },
        tableColumn: true,
        filterName: "reporterFilter",
        type: "text",
         minWidth: "230px",
    },
    {
        ...commonColumns,
        name: "Assignee",
        key: "assignee",
        selector: (row, index) => {
            return (
                <div style={{ display: "inline-block", overflow: "visible", textTransform: "capitalize" }}>
                    <div className='d-flex align-items-center' style={{ cursor: "pointer" }}>
                        <UserAvatar username={row?.assignee?.username} image={row?.assignee?.image} marginRight="7px" />
                        <span className="hyperlink">{row?.assignee?.username}</span>
                    </div>
                </div>
            )
        },
        tableColumn: true,
        filterName: "assigneeFilter",
        type: "text",
         minWidth: "230px",
    },
    {
        ...commonColumns,
        name: "Status",
        key: "status",
        selector: (row, index) => (
            <div style={{ display: "inline-block", overflow: "visible" }}>
                <div
                    className={`status ${row?.status}`}
                    style={{ cursor: "pointer", textTransform: "capitalize" }}
                >
                    <div className='d-flex align-items-center gap-1'>
                        {row?.status === "open" ? openIcon : row?.status === "closed" ? closedIcon : row?.status === "resolved" ? resolvedIcon : row?.status === "completed" ? resolvedIcon : row?.status === "in_progress" ? inProgressIcon : null}
                        {row?.status}
                    </div>
                </div>
            </div>
        ),
        tableColumn: true,
        filterName: "statusFilter",
        type: "text",
        width: "120px",
    }
].filter(Boolean);

const IssuesTableModal = ({ show, onHide, userId, username }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [issuesData, setIssuesData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(100);

    useEffect(() => {
        if (show && userId) {
            setLoading(true);
            dispatch(fetchUserIssues({
                page: currentPage,
                limit,
                userId: userId
            }))
                .unwrap()
                .then(res => setIssuesData(res?.data))
                .finally(() => setLoading(false));
        }
    }, [show, userId, currentPage, limit, dispatch]);

    const columns = columnsTemplate;

    return (
        <CustomModal
            show={show}
            handleClose={onHide}
            size="xl"
            heading={`Assigned Tasks to "${username || ""}"`}
        >
            {loading ? (
                <div className="loading_spinner"><Spinner animation="grow" /></div>
            ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <NewTableComponent
                        columns={columns}
                        data={issuesData?.results || []}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        totalItems={issuesData?.meta?.totalItems}
                        totalPages={issuesData?.meta?.totalPages}
                        loading={loading}
                        showAddBtn={false}
                        showPagination={false}
                        showFilter={false}
                    />
                </div>
            )}
        </CustomModal>
    );
};

export default IssuesTableModal;
